import { useEffect, useRef, useState } from 'react';
import { listPdv } from '../api/pdv';
import type { PdvRow } from '../lib/db-types';

export interface PdvElegido {
  id: string;
  etiqueta: string;
}

export function etiquetaPdv(p: Pick<PdvRow, 'code' | 'name' | 'chain'>): string {
  return `${p.code} — ${p.name}${p.chain ? ` (${p.chain})` : ''}`;
}

/**
 * Buscar y elegir una tienda en un solo campo.
 *
 * Reemplaza al par «Buscar PDV» + desplegable del formulario de horarios, que filtraba bien (los
 * registros del 25-set lo muestran) pero solo achicaba las opciones de un desplegable cerrado: al
 * escribir no cambiaba nada que se viera, y parecía roto. Ahora las coincidencias aparecen debajo.
 *
 * Cada tecla dispara una búsqueda. Una respuesta que llega después de que empezó otra, o después de
 * que se cerró el modal, se DESCARTA: si no, una búsqueda vieja pisaría la nueva, y peor, una que
 * volviera con una sola tienda la elegiría en el horario que se abrió después (el callback que queda
 * vivo actualiza el formulario que esté abierto en ese momento).
 */
export default function BuscadorPdv({ elegido, onElegir }: {
  elegido: PdvElegido | null;
  onElegir: (p: PdvElegido | null) => void;
}) {
  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState<PdvRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const onElegirRef = useRef(onElegir);
  onElegirRef.current = onElegir;
  const inputRef = useRef<HTMLInputElement>(null);
  const cambiarRef = useRef<HTMLButtonElement>(null);
  // A dónde va el foco después de elegir o de soltar. Solo lo marca una acción de quien usa el
  // formulario: abrir un horario para editar no mueve el foco.
  const moverFoco = useRef<'cambiar' | 'campo' | null>(null);

  useEffect(() => {
    const termino = texto.trim();
    if (termino === '') {
      setResultados(null);
      setError(null);
      return;
    }
    let vigente = true;
    listPdv(termino)
      .then((rows) => {
        if (!vigente) return;
        // Solo se programan tiendas activas: una inactiva se ofrecería para un horario que no se
        // debe crear.
        const activas = rows.filter((p) => p.active);
        setError(null);
        if (activas.length === 1) {
          moverFoco.current = 'cambiar';
          onElegirRef.current({ id: activas[0].id, etiqueta: etiquetaPdv(activas[0]) });
          setTexto('');
          return;
        }
        setResultados(activas);
      })
      .catch((e: unknown) => {
        if (!vigente) return;
        setResultados(null);
        setError(e instanceof Error ? e.message : 'No se pudo buscar la tienda.');
      });
    return () => { vigente = false; };
  }, [texto]);

  useEffect(() => {
    if (moverFoco.current === 'cambiar' && elegido) {
      cambiarRef.current?.focus();
      moverFoco.current = null;
    } else if (moverFoco.current === 'campo' && !elegido) {
      inputRef.current?.focus();
      moverFoco.current = null;
    }
  }, [elegido]);

  function elegir(p: PdvRow) {
    moverFoco.current = 'cambiar';
    onElegir({ id: p.id, etiqueta: etiquetaPdv(p) });
    setTexto('');
  }

  function soltar() {
    moverFoco.current = 'campo';
    onElegir(null);
  }

  return (
    <div className="text-sm mb-2">
      {elegido
        ? <span className="block">PDV</span>
        : <label htmlFor="buscar-pdv" className="block">PDV</label>}
      {elegido ? (
        <div className="flex items-center justify-between gap-2 rounded border border-blue-200 bg-blue-50 px-2 py-1">
          <span>{elegido.etiqueta}</span>
          <button ref={cambiarRef} type="button" aria-label={`Cambiar tienda (${elegido.etiqueta})`}
            className="text-xs text-blue-700 underline" onClick={soltar}>
            Cambiar
          </button>
        </div>
      ) : (
        <input
          ref={inputRef}
          id="buscar-pdv"
          className="w-full rounded border px-2 py-1"
          placeholder="Código, nombre, distrito o cadena…"
          autoComplete="off"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
      )}
      {/* Área de estado: lo que encontró o lo que eligió se anuncia a quien usa lector de pantalla. */}
      <div role="status" className="mt-1 text-xs">
        {elegido ? (
          <span className="sr-only">Tienda elegida: {elegido.etiqueta}.</span>
        ) : error ? (
          <p className="text-red-700">No se pudo buscar: {error}</p>
        ) : resultados === null ? (
          <p className="text-gray-500">Escribe el código, el nombre, el distrito o la cadena de la tienda.</p>
        ) : resultados.length === 0 ? (
          <p className="text-amber-700">Ninguna tienda activa coincide con «{texto.trim()}».</p>
        ) : (
          <p className="text-gray-500">
            {resultados.length} {resultados.length === 1 ? 'tienda coincide' : 'tiendas coinciden'}.
          </p>
        )}
      </div>
      {!elegido && !error && resultados !== null && resultados.length > 0 && (
        <ul aria-label="Tiendas que coinciden" className="mt-1 max-h-48 divide-y overflow-y-auto rounded border">
          {resultados.map((p) => (
            <li key={p.id}>
              <button type="button" onClick={() => elegir(p)}
                className="w-full px-2 py-1 text-left hover:bg-gray-100">
                {etiquetaPdv(p)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
